import { valueSetByLanguage } from '@upright/common/model/input/impacts';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ImpactLanguage, ModelEntityType } from '../../../../../../../common/model/input/types';
import { FinalImpactScoreType, NetScoreFormat } from '../../../../../../../common/model/output/scores/types';
import { setValuesSelectorOpen } from '../../../../../../untidy/redux/actions/actions';
import { getValueSetName, getValuesSelectorOpen } from '../../../../../../untidy/redux/selectors';
import InfoTooltipWithIcon from '../../../../../generic/components/tooltips/info-tooltip-with-icon';
import { BouColor } from '../../../../../generic/global/bou-colors';
import { useValueSetSelectorAvailable } from '../../../../../generic/hooks';
import { scoreTypeTitle } from '../aggregate-metrics';
import { NetScoreExplanation } from '../net-impact-chart/bottom/common/net-score-block/net-score-explanation';

interface Props {
  type: FinalImpactScoreType;
  netScoreFormat: NetScoreFormat;
  valueSetName?: string;
  hasTooltip?: boolean;
  entityType: ModelEntityType;
  className?: string;
  language?: ImpactLanguage;
  disableValueSetSelector?: boolean;
}

export function NetScoreTypeAndValueSet(props: Props) {
  const activeValueSet = useSelector(getValueSetName);
  const valueSetName = props.valueSetName || activeValueSet; // default to active value set
  const language = props.language || ImpactLanguage.en;
  const localizedValueSetName = get(valueSetByLanguage, language, undefined)?.[valueSetName] || valueSetName;
  const isActive = valueSetName === activeValueSet;
  const open = useSelector(getValuesSelectorOpen);
  const dispatch = useDispatch();
  // The caption names the active value set, so it goes wherever the selector does — including the
  // profile images the render API produces, which share this component.
  const valueSetAvailable = useValueSetSelectorAvailable();

  return (
    <Root className={props.className}>
      <Top
        style={{
          color: isActive ? BouColor.BLACK : BouColor.TOOL_TEXT_GRAY,
        }}
      >
        {scoreTypeTitle(props.netScoreFormat)}
        {props.hasTooltip && (
          <InfoTooltipWithIcon
            style={{ marginLeft: '0.65em' }}
            maxWidth={'350px'}
            content={
              <NetScoreExplanation
                netScoreFormat={props.netScoreFormat}
                type={props.entityType}
                valueSetName={valueSetName}
              />
            }
          />
        )}
      </Top>
      {valueSetAvailable && (
        <Bottom
          isClickable={!props.disableValueSetSelector}
          style={{
            color: isActive ? BouColor.GREY_B3B3B3 : BouColor.TOOL_TEXT_GRAY,
          }}
          onClick={() => {
            if (props.disableValueSetSelector) return;
            dispatch(setValuesSelectorOpen(!open));
          }}
        >
          <ValueSetTitle>{getLocalizedValueSetTerm(language)}:&nbsp;</ValueSetTitle>
          <ValueSetName>{localizedValueSetName}</ValueSetName>
        </Bottom>
      )}
    </Root>
  );
}

function getLocalizedValueSetTerm(impactLang: ImpactLanguage) {
  switch (impactLang) {
    case ImpactLanguage.fi:
      return 'Arvot';
    case ImpactLanguage.en:
      return 'Value set';
    case ImpactLanguage.sv:
      return 'Värdeuppsättning';
  }
}

const Top = styled.div`
  font-weight: bold;
  color: ${BouColor.BLACK};
  display: flex;
  align-items: center;
`;

const Bottom = styled.div<{ isClickable: boolean }>`
  font-size: 87.5%;
  margin-top: 0.2em;
  color: ${BouColor.GREY_B3B3B3};
  cursor: ${props => props.isClickable && 'pointer'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 13em;
  &:hover {
    color: ${props => props.isClickable && BouColor.BLACK};
  }
`;

const ValueSetTitle = styled.span``;
const ValueSetName = styled.span`
  font-weight: 600;
`;

const Root = styled.div``;
